import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { assets } from '../config/assets'
import { useRidersHelped, formatNumber } from '../hooks/useRidersHelped'
import { SCOTT_PERSON, AH_ORGANIZATION } from '../config/schema'
import { buildRidersWayStartUrl } from '../lib/ridersWay'


export function AboutPage() {
  return (
    <Layout>
      <ManagedSeo path="/about" jsonLd={[SCOTT_PERSON, AH_ORGANIZATION]} />
      <HeroSection />
      <MissionSection />
      <FourFactorsSection />
      <CredentialsSection />
      <BioSection />
      <CtaSection />
    </Layout>
  )
}

// ─── SECTION 1: HERO ───────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section className="relative bg-navy py-24 lg:py-36 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <img
        src={assets.scott.portraitChestnutLeading}
        alt=""
        aria-hidden="true"
        width={1920}
        height={1080}
        fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-navy/80" aria-hidden="true" />
      <div className="relative max-w-4xl mx-auto text-center">
        <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-4">
          About Scott Purdum
        </p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-semibold leading-tight mb-6">
          Horse Behaviorist.{' '}
          <span className="text-gold">Rider Confidence Coach.</span>
        </h1>
        <p className="font-body text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
          Fredericksburg, Virginia. Over two decades helping riders stop working
          on the wrong thing and start building real partnerships with their
          horses.
        </p>
      </div>
    </section>
  )
}

// ─── SECTION 2: MISSION ────────────────────────────────────────────────────

function MissionSection() {
  const ridersHelped = useRidersHelped()

  return (
    <section className="bg-navy py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <div className="w-16 h-0.5 bg-gold mx-auto mb-10" aria-hidden="true" />

        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-semibold leading-snug mb-8">
          One mission: help one million people become confident communicators
          with their horses.
        </h2>

        {ridersHelped !== null && (
          <div className="mb-10">
            <p className="font-body text-6xl sm:text-7xl text-gold leading-none mb-2 font-bold">
              {formatNumber(ridersHelped)}
            </p>
            <p className="font-body text-sm font-semibold tracking-widest uppercase text-white/50">
              Riders Helped
            </p>
          </div>
        )}

        <p className="font-body text-base sm:text-lg text-white/70 leading-relaxed mb-6">
          Most riders assume the horse is the problem. The horse is spooky. The
          horse won't listen. The horse is too much. Scott's entire career is
          built on a different premise:
        </p>
        <p className="font-heading text-2xl sm:text-3xl text-gold leading-snug">
          The horse is rarely the problem. The human and the partnership usually
          are.
        </p>
      </div>
    </section>
  )
}

// ─── SECTION 3: HOW SCOTT WORKS ────────────────────────────────────────────

function FourFactorsSection() {
  return (
    <section className="bg-white py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center">
          <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
            How Scott Works
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-navy font-semibold mb-8">
            Every Problem Has a System. Every System Has a Backup.
          </h2>
          <div className="mb-4">
            <img
              src={assets.scott.foreheadToHorseBw}
              alt="Scott Purdum connecting forehead to forehead with a horse in a quiet moment"
              width={800}
              height={500}
              loading="lazy"
              className="w-full max-w-2xl mx-auto max-h-[280px] object-cover object-center rounded-lg"
            />
          </div>
          <p className="font-body italic text-sm text-navy/50 mb-10">
            No dead ends. No guessing. Just the next right step.
          </p>
          <p className="font-body text-base text-navy/70 max-w-2xl mx-auto leading-relaxed">
            Whether Scott is working through a confidence issue or a training
            problem, there is always a structured system to follow. Not a list
            of tips. Not a guess. A step by step process built around
            understanding what is actually driving the behavior. And when one
            system does not produce the result, there is always another
            waiting. That is what separates riders who get stuck from riders
            who keep moving forward. Not talent, not experience. A system.
          </p>
        </div>
      </div>
    </section>
  )
}

// ─── SECTION 4: CREDENTIALS ────────────────────────────────────────────────

function CredentialsSection() {
  return (
    <section className="bg-navy py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14 lg:mb-20">
          <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-3">
            On Screen and On Air
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-semibold mb-4">
            A recognized voice in the industry.
          </h2>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto leading-relaxed">
            Scott's work reaches riders far beyond the arena. Through national
            television and his own podcast, he brings the same structured,
            no-nonsense approach to audiences across the country.
          </p>
        </div>

        {/* Liberty action photo */}
        <div className="overflow-hidden rounded-lg mb-12">
          <img
            src={assets.scott.groundworkLiberty}
            alt="Scott Purdum working liberty with a horse in the arena"
            width={1200}
            height={500}
            loading="lazy"
            className="w-full max-h-[280px] object-cover object-center"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
          {/* Card 1: Advantage Horsemanship TV */}
          <div className="border border-gold/30 p-8 flex flex-col items-start">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
              <BroadcastIcon />
            </div>
            <h3 className="font-heading text-xl text-white font-semibold mb-3">
              Advantage Horsemanship TV
            </h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">
              16 seasons on RFD-TV and The Cowboy Channel. Scott's long-running
              television series bringing structured horsemanship into living
              rooms nationwide.
            </p>
          </div>

          {/* Card 2: Horse or Home */}
          <div className="border border-gold/30 p-8 flex flex-col items-start">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
              <TvIcon />
            </div>
            <h3 className="font-heading text-xl text-white font-semibold mb-3">
              Horse or Home
            </h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">
              A new television series following the real decisions families face
              when the horse-rider partnership is on the line.
            </p>
          </div>

          {/* Card 3: Horse or Human Podcast */}
          <div className="border border-gold/30 p-8 flex flex-col items-start">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-6">
              <MicrophoneIcon />
            </div>
            <h3 className="font-heading text-xl text-white font-semibold mb-3">
              Horse or Human: The Podcast
            </h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">
              Breaking down the communication gap between horse and rider, one
              episode at a time. Available wherever you listen.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// --- Credential Icons ---

function BroadcastIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6 text-gold"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.25a.375.375 0 11-.75 0 .375.375 0 01.75 0v0z"
      />
    </svg>
  )
}

function TvIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6 text-gold"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 20.25h12m-7.5-3v3m3-3v3m-10.125-3h17.25c.621 0 1.125-.504 1.125-1.125V4.875c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125z"
      />
    </svg>
  )
}

function MicrophoneIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6 text-gold"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
      />
    </svg>
  )
}

// ─── SECTION 5: BIO ────────────────────────────────────────────────────────

function BioSection() {
  return (
    <section className="bg-white py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Photo */}
          <div className="aspect-[4/5] w-full overflow-hidden">
            <img
              src={assets.scott.bondingHorseBw}
              alt="Scott Purdum bonding with a horse, smiling"
              width={640}
              height={800}
              loading="lazy"
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Text */}
          <div>
            <p className="font-body text-gold text-xs font-semibold tracking-widest uppercase mb-4">
              The Philosophy
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-navy font-semibold leading-snug mb-6">
              It was never about the horse.
            </h2>
            <div className="space-y-5">
              <p className="font-body text-sm text-navy/70 leading-relaxed">
                I have spent over twenty years in the arena, on the trail, and
                at the barn, working with horses and the people who ride them.
                And I will tell you straight: the breakthroughs never come from
                fixing the horse.
              </p>
              <p className="font-body text-sm text-navy/70 leading-relaxed">
                They come from the moment you realize you have been working on
                the wrong thing. The moment you stop blaming the horse for
                being spooky or resistant and start asking what you are
                actually communicating. That shift, from blame to ownership,
                is where everything changes.
              </p>
              <p className="font-body text-sm text-navy/70 leading-relaxed">
                I built the Four Factors Framework to give you a diagnostic
                tool. Instead of guessing, you evaluate four variables: the
                horse, the human, the connection, and the environment. The
                framework tells you which one is failing so you can fix the
                right thing. Every coaching program, every masterclass, and every
                interaction with the Rider's Way app is built on this
                structure.
              </p>
              <p className="font-body text-sm text-navy/70 leading-relaxed">
                My work spans every discipline. Western, English, trail,
                groundwork, liberty. The principles do not change because the
                saddle does. Communication is communication.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-8">
              <img
                src={assets.scott.signature}
                alt="Scott Purdum signature"
                width={180}
                height={60}
                loading="lazy"
                className="h-12 w-auto opacity-70"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── SECTION 6: CTA ────────────────────────────────────────────────────────

function CtaSection() {
  return (
    <section className="relative bg-navy py-16 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <img
        src={assets.scott.ridingChestnutCanter}
        alt=""
        aria-hidden="true"
        width={1920}
        height={1080}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-center opacity-20"
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-4xl sm:text-5xl text-white font-semibold mb-4">
          Ready to work with Scott?
        </h2>
        <p className="font-body text-base text-white/70 leading-relaxed mb-8 max-w-xl mx-auto">
          Start with the Rider's Way app. Seven days free. A personalized plan
          built around you and your horse, so you stop guessing and start
          communicating.
        </p>
        <a
          href={buildRidersWayStartUrl('about_page_cta')}
          className="font-body text-base font-semibold bg-gold text-navy px-10 py-4 min-h-[52px] inline-flex items-center justify-center hover:opacity-90 transition-opacity duration-150 uppercase tracking-wide"
        >
          Start Your Free 7-Day Trial
        </a>
        <p className="font-body text-xs text-white/40 mt-4">
          $19.99/month after trial · Cancel anytime
        </p>
      </div>
    </section>
  )
}
